/**
 * The access a login holds on each project, as editable rows, and the changes between what was loaded and what is on
 * screen. Pure: no network, no React. '' means "no project role, the workspace role applies".
 */
import { ROLE_LABELS, roleLevel } from '../../utils/roles';
import type { UserRole } from '../../types/User';
import type { ProjectAccessRole, ProjectMember } from '../../services/ProjectMemberService';

export type AccessValue = '' | ProjectAccessRole;

export interface AccessRow {
  projectId: string;
  projectName: string;
  /** What is saved on the server. */
  initial: AccessValue;
  /** What is chosen on screen. */
  value: AccessValue;
  /** The login's role for the whole workspace (what '' means). */
  workspaceRole: UserRole | null;
  /** Set when this project's members could not be read: the row is shown but left alone. */
  loadError: string | null;
}

const ORDER: readonly ProjectAccessRole[] = ['admin', 'planner', 'contractor', 'viewer', 'none'];
export const ACCESS_LABELS: Readonly<Record<ProjectAccessRole, string>> = { ...ROLE_LABELS, none: 'No access' };

export interface AccessOption { value: AccessValue; label: string }

/** Options for one row, never above the signed-in person's own level (the API refuses those anyway). */
export function accessOptions(workspaceRole: UserRole | null, callerRole: string | null | undefined): AccessOption[] {
  const ceiling = roleLevel(callerRole);
  const fallback = workspaceRole ? `Workspace role (${ROLE_LABELS[workspaceRole]})` : 'Workspace role';
  return [
    { value: '', label: fallback },
    ...ORDER.filter(r => r === 'none' || roleLevel(r) <= ceiling).map(r => ({ value: r as AccessValue, label: ACCESS_LABELS[r] })),
  ];
}

/** True when the signed-in person may not change this row (the login is above them). */
export const isAboveCaller = (workspaceRole: UserRole | null, callerRole: string | null | undefined): boolean =>
  roleLevel(workspaceRole) > roleLevel(callerRole);

/** A row from one project's member list; `member` is the login's entry there (missing: the workspace role applies). */
export function rowFor(projectId: string, projectName: string, member: ProjectMember | undefined, workspaceRole: UserRole | null): AccessRow {
  const initial: AccessValue = member?.project_role ?? '';
  return { projectId, projectName, initial, value: initial, workspaceRole: member?.workspace_role ?? workspaceRole, loadError: null };
}

export const unreadableRow = (projectId: string, projectName: string, workspaceRole: UserRole | null): AccessRow => ({
  projectId, projectName, initial: '', value: '', workspaceRole,
  loadError: "Couldn't read access on this project. It is left as it is.",
});

export const changedRows = (rows: readonly AccessRow[]): AccessRow[] => rows.filter(r => !r.loadError && r.value !== r.initial);
